/**
 * v5 long-tail registrations — corner cabinetry, blank panels, fridge
 * surround, niche, switch family, outlet family, sconce, overhead-wall-light, stairs,
 * washer/dryer. Imported from index.js so all type → component
 * bindings happen in one place at module load.
 *
 * Each tier exports its registrations as a function called from
 * index.js so the main thread can consolidate without needing to
 * touch this file.
 */
import { registerSchematic } from './registry';
import { CornerCabinetSchematic } from './CornerCabinetSchematic';
import { CornerFillCabinetSchematic } from './CornerFillCabinetSchematic';
import { BlankPanelSchematic } from './BlankPanelSchematic';
import { FridgeSurroundSchematic } from './FridgeSurroundSchematic';
import { WallHungSinkSchematic } from './WallHungSinkSchematic';
import { ShowerNicheSchematic } from './ShowerNicheSchematic';
import { ShowerAccessorySchematic } from './ShowerAccessorySchematic';
import {
  SwitchSchematic,
  DimmerSchematic,
  ThreeWaySchematic,
} from './SwitchSchematic';
import {
  OutletSchematic,
  GfciOutletSchematic,
  Outlet240vSchematic,
} from './OutletSchematic';
import { SurfaceOutletSchematic } from './SurfaceOutletSchematic';
import {
  ExhaustFanSchematic,
  SmokeDetectorSchematic,
} from './CeilingDeviceSchematics';
import { WallSconceSchematic } from './WallSconceSchematic';
import { VanityLightSchematic } from './VanityLightSchematic';
import { StairsSchematic } from './StairsSchematic';
import {
  WasherSchematic,
  DryerSchematic,
  WasherDryerSchematic,
} from './LaundrySchematic';

export function registerV5Schematics() {
  registerSchematic('corner-cabinet', CornerCabinetSchematic);
  registerSchematic('corner-fill-cabinet', CornerFillCabinetSchematic);
  registerSchematic('blank-panel', BlankPanelSchematic);
  registerSchematic('fridge-surround', FridgeSurroundSchematic);
  // An oven surround IS a fridge surround in 3D — proceduralRenderers/index.js
  // maps both to `fridgeSurroundRenderer`, since both are two jamb panels
  // flanking a slot. Sharing the schematic keeps the elevation matching rather
  // than growing a second copy that can drift.
  registerSchematic('oven-surround', FridgeSurroundSchematic);
  // A laundry surround is the same two panels around a washer/dryer
  // (buildLaundrySurround writes the same sidePanelThickness); the 3D side
  // maps it to fridgeSurroundRenderer too.
  registerSchematic('laundry-surround', FridgeSurroundSchematic);
  // Gallery key for the procedural wall-hung basin. A real object is
  // type 'sink' + sinkType 'wall-mount', which SinkSchematic hands to the
  // same component.
  registerSchematic('wall-hung-sink', WallHungSinkSchematic);
  registerSchematic('shower-niche', ShowerNicheSchematic);
  // a shower system's accessories and the standard grab bar (step 3a)
  registerSchematic('shower-shelf', ShowerAccessorySchematic);
  registerSchematic('corner-caddy', ShowerAccessorySchematic);
  registerSchematic('shower-seat', ShowerAccessorySchematic);
  registerSchematic('grab-bar', ShowerAccessorySchematic);
  registerSchematic('shower-bench', ShowerAccessorySchematic);
  registerSchematic('switch', SwitchSchematic);
  registerSchematic('dimmer', DimmerSchematic);
  registerSchematic('3way', ThreeWaySchematic);
  registerSchematic('outlet', OutletSchematic);
  registerSchematic('gfci-outlet', GfciOutletSchematic);
  registerSchematic('outlet-240v', Outlet240vSchematic);
  registerSchematic('surface-outlet', SurfaceOutletSchematic);
  registerSchematic('exhaust-fan', ExhaustFanSchematic);
  registerSchematic('smoke-detector', SmokeDetectorSchematic);
  // `wall-light` is the canonical sconce type (see
  // proceduralRenderers/index.js); the vanity-assembly pipeline emits
  // objects with `type: 'wall-light'` and `type: 'overhead-wall-light'`.
  registerSchematic('wall-light', WallSconceSchematic);
  registerSchematic('overhead-wall-light', VanityLightSchematic);
  registerSchematic('stairs', StairsSchematic);
  registerSchematic('washer', WasherSchematic);
  registerSchematic('dryer', DryerSchematic);
  // RoomPlan's combined appliance. Registry keys are matched lowercased,
  // same as the procedural renderer registry.
  registerSchematic('washerdryer', WasherDryerSchematic);
}
